import type { Table } from "apache-arrow";

import type {
  GroupDimensionValue,
  RenderedGroup,
  EntityInteractionPrecondition,
  EntityResultContext,
  ExactValueType,
  GroupDimensionMapping,
  MarkLineageInteractionPrecondition,
  TemporalProvenanceLookup,
  ExactValueLookup,
} from "./markLineageTypes";

export const INTERACTION_TIME_UNITS = ["s", "ms", "us", "ns"] as const;
export type InteractionTimeUnit = (typeof INTERACTION_TIME_UNITS)[number];

export type InteractionValueType =
  | { type: "string" | "boolean" | "number" | "integer"; nullable?: boolean }
  | {
      type: "instant";
      unit: InteractionTimeUnit;
      epoch: string;
      nullable?: boolean;
    };

/**
 * Where an instant was read from: its topic, partition and timeline source, and the partition's time offset in
 * nanoseconds. The timeline source is distinct from the epoch the instant's ticks count from.
 */
export interface TimelineProvenance {
  topicId: string;
  partitionId: string;
  timelineSource: string;
  offsetNs: bigint;
}

export type InteractionValue =
  | { type: "null" }
  | { type: "string"; value: string }
  | { type: "boolean"; value: boolean }
  | { type: "number"; value: number }
  | { type: "integer"; value: bigint }
  | {
      type: "instant";
      ticks: bigint;
      unit: InteractionTimeUnit;
      epoch: string;
      provenance?: TimelineProvenance;
    };

export type SerializedInteractionValue =
  | Exclude<InteractionValue, { type: "integer" | "instant" }>
  | { type: "integer"; value: string }
  | {
      type: "instant";
      ticks: string;
      unit: InteractionTimeUnit;
      epoch: string;
      provenance?: Omit<TimelineProvenance, "offsetNs"> & { offsetNs: string };
    };

export interface InteractionKey {
  key: string;
  valueType: InteractionValueType;
}

export interface SelectionDeclaration {
  name: string;
  /**
   * With no keys, each clause is a `row` clause naming one row of one result revision by its row key. With keys,
   * a clause carries values of these keys, which each panel maps onto its own fields.
   */
  keys: InteractionKey[];
  resolution?: "single" | "intersect" | "crossfilter";
}

export interface FieldRef {
  bindingName: string;
  field: string;
}

export interface KeyFieldMapping {
  key: string;
  field: string;
}

export interface SelectionKeyMapping {
  selection: string;
  /** With no keys, only a `row` clause naming this table's own result revision applies; another revision is refused. */
  keys: KeyFieldMapping[];
  dimensions?: GroupDimensionMapping[];
}

export interface SelectionBinding extends SelectionKeyMapping {
  bindingName: string;
}

export const INTERACTION_ENTITY_TYPES = [
  "datasets",
  "sessions",
  "events",
  "files",
  "devices",
  "collections",
] as const;
export type InteractionEntityType = (typeof INTERACTION_ENTITY_TYPES)[number];

/** A subject key and the field it reads its value from. */
export type SubjectKey = InteractionKey & { field: string };

/**
 * A subject key of a `span` subject: the range from the value of its `field` (included) to the value of its
 * `endField` (excluded), both columns of the clicked row.
 */
export type SpanKey = SubjectKey & { endField: string };

export type InteractionSubjectDeclaration = {
  bindingName: string;
  /** Present only when the key fields are output fields of this mark lineage rather than columns of the bound stage. */
  markLineageId?: string;
} & (
  | { kind: "row" | "values" | "group" | "interval"; keys: SubjectKey[] }
  /** A range from two columns of the clicked row, published as an `interval` subject. */
  | { kind: "span"; keys: SpanKey[] }
  | { kind: "entity"; entityType: InteractionEntityType; keys: SubjectKey[] }
);

export type SelectionOperation =
  | "replace"
  | "toggle"
  | "add"
  | "remove"
  | "clear";
export type ControlOperation = SelectionOperation | "reset";
export type InteractionTrigger = "primary" | "secondary" | "interval-complete";

export type NavigateDestination =
  | { kind: "entity"; entityType: InteractionEntityType; idKey: string }
  | {
      kind: "board";
      boardId: string;
      inputs: { control: string; sourceKey: string }[];
      includeTimeRange?: boolean;
    };

export interface DetailTarget {
  stage: string;
  keys: { sourceKey: string; targetField: string }[];
  dimensions?: GroupDimensionMapping[];
  /**
   * How each `filter` selection applied to the displayed result maps onto the target stage's fields, so the same
   * selections narrow the contributing rows.
   */
  selectionBindings?: SelectionKeyMapping[];
}

export type PanelInteraction = {
  id: string;
  label: string;
  trigger: InteractionTrigger;
  subject: InteractionSubjectDeclaration;
} & (
  | {
      kind: "selection";
      selection: string;
      operation: SelectionOperation;
      scope?: "local" | "outer";
    }
  | {
      kind: "control";
      control: string;
      key: string;
      operation: ControlOperation;
    }
  | {
      kind: "detail";
      mode: "output-row" | "matching-output" | "contributing-rows";
      target?: DetailTarget;
    }
  | { kind: "navigate"; destination: NavigateDestination }
);

export interface ResultRevision {
  stage: string;
  generation: string;
  schema: string;
}

export interface TaggedKeyValue {
  key: string;
  value: InteractionValue;
}

export interface TaggedInterval {
  key: string;
  /** The name the chart shows for the key's field, such as the title of
   *  the axis that draws it. The control bar names the key by it in the
   *  entry for the published interval. */
  title?: string;
  lower: InteractionValue;
  upper: InteractionValue;
  lowerInclusive: boolean;
  upperInclusive: boolean;
}

export type InteractionSubject =
  | {
      kind: "values";
      values: TaggedKeyValue[][];
      /** The name of what the values stand for, as the clicked row carries
       *  it: a session's name beside its id. A selection that holds only
       *  these values shows the name in place of them. */
      label?: string;
    }
  | {
      kind: "row";
      revision: ResultRevision;
      rowKey: string;
      values: TaggedKeyValue[];
      /** The name of what the row or its values stand for, as the row
       *  carries it: a session's name beside its id. A selection that holds
       *  only this row, or only its values, shows the name in their place. */
      label?: string;
    }
  | {
      kind: "group";
      revision: ResultRevision;
      values: TaggedKeyValue[];
      renderedGroup?: RenderedGroup;
      /** The name of what the values stand for, as the clicked row carries
       *  it: a session's name beside its id. A selection that holds only
       *  these values shows the name in place of them. */
      label?: string;
    }
  | {
      kind: "interval";
      /** An empty list asks an `interval-complete` selection interaction to clear its selection. */
      intervals: TaggedInterval[];
    }
  | {
      kind: "entity";
      entityType: InteractionEntityType;
      id: InteractionValue;
      /** The entity's name as the panel shows it. A selection that holds
       *  only this entity shows the name in place of the id. */
      label?: string;
    };

export interface InteractionDiagnostic {
  code: string;
  path: (string | number)[];
  /** The problem as the chat reads it, which may name the board JSON's
   *  keys. */
  message: string;
  /** The problem as people read it, naming each setting the way the panel
   *  editor does; absent where `message` already does. */
  forPeople?: string;
}

/** The problem as a person reads it beside the board. */
export function diagnosticForPeople(diagnostic: InteractionDiagnostic): string {
  return diagnostic.forPeople ?? diagnostic.message;
}

/** The diagnostics as one text a person reads beside the board. Diagnostics
 *  whose `diagnosticForPeople` texts match appear once, as when the mark
 *  lineages of a chart's two layers report one problem with one column. */
export function diagnosticsForPeople(
  diagnostics: readonly InteractionDiagnostic[],
): string {
  return [...new Set(diagnostics.map(diagnosticForPeople))].join(" ");
}

export interface InteractionDependency {
  stage: string;
  dependent:
    | { kind: "render"; panel: string; bindingName: string }
    | { kind: "detail"; panel: string; interactionId: string }
    | { kind: "domain"; control: string };
}

export interface ResultContextConstraint {
  id: string;
  selection: string;
  scope: string;
  source: string;
  clients: string[];
  resolution: "single" | "intersect" | "crossfilter";
  mode: "filter" | "highlight" | "ignore";
  keys: KeyFieldMapping[];
  dimensions?: GroupDimensionMapping[];
  clause: SerializedSelectionClause;
  publisherExcluded: boolean;
}

export type SerializedSelectionClause =
  | { kind: "group"; markLineageId: string; groups: GroupDimensionValue[][] }
  | { kind: "point"; keys: string[]; values: SerializedInteractionValue[][] }
  | {
      kind: "interval";
      intervals: {
        key: string;
        lower: SerializedInteractionValue;
        upper: SerializedInteractionValue;
        lowerInclusive: boolean;
        upperInclusive: boolean;
      }[];
    }
  | { kind: "row"; revision: ResultRevision; rowKey: string };

/**
 * Which query-cache answers a source stage's rows came from, in one run of the source: an entity or metric source
 * reads one answer, a topic source one for its scope's entities, one for each entity's topic and one for each read
 * plan. Two runs that read the same answers give equal fetches.
 */
export interface SourceFetch {
  /** The id of the source stage. */
  stage: string;
  /** A digest of the query key of every answer the run read and when each answer arrived. */
  digest: string;
  /** When the latest of those answers arrived, in Unix milliseconds. */
  fetchedAt: number;
}

/** What a result's rows were computed from. */
export interface ComputedFrom {
  /**
   * The stage the rows are the rows of and every stage it reads, directly or through other stages, each with a digest
   * of its declaration key. The digest changes when the stage or a stage it reads is declared differently, including
   * when a control value written into one of them changes.
   */
  stages: { stage: string; declarationDigest: string }[];
  /**
   * Each fetch a source among those stages took its rows from. A source read twice for one result, with its answers
   * fetched again in between, appears with both fetches.
   */
  fetches: SourceFetch[];
}

export interface ResultContext {
  orgId: string;
  instanceId: string;
  definitionDigest: string;
  result: ResultRevision;
  timeRange: { startMs: number; endMs: number };
  controlValues: Record<string, SerializedInteractionValue[]>;
  /**
   * The selection clauses applied to this result, each already mapped onto its fields. Every `filter` constraint
   * whose publisher is not excluded applies (AND); the value tuples within one point clause are alternatives (OR).
   */
  constraints: ResultContextConstraint[];
  computedFrom: ComputedFrom;
  coverage: {
    stage: string;
    status: "complete" | "limited" | "unknown";
    limit?: number;
    message?: string;
  }[];
}

/**
 * Reads a result's values in their source types (integers as `bigint`, instants as ticks) from its Arrow table. Row
 * keys come from row positions, never from the numbers the renderer draws.
 */
export interface TypedResultValues {
  revision: ResultRevision;
  fields: { field: string; valueType: InteractionValueType }[];
  exactFields?: { field: string; valueType: ExactValueType }[];
  /** Each uniquely named column whose values no value type describes,
   *  exactly or not: a list, map or struct column, say. A column of the Null
   *  type, which holds no values, is not one. */
  unreadableFields: string[];
  row(rowKey: string): Readonly<Record<string, InteractionValue>> | undefined;
  /** The one value `row` gives for `field`, read without the row's other cells. */
  value(rowKey: string, field: string): InteractionValue | undefined;
  exactValue(rowKey: string, field: string): ExactValueLookup;
  provenance(rowKey: string, field: string): TemporalProvenanceLookup;
}

export interface RetainedResultHandle {
  revision: ResultRevision;
  release(): Promise<void>;
}

export interface ResultRef {
  revision: ResultRevision;
  values: TypedResultValues;
  resultContext: ResultContext;
  /** Acquire synchronously, before any asynchronous detail work. */
  retain(): RetainedResultHandle | undefined;
  /**
   * The row with this key as a one-row slice of the result's Arrow table, with its source-row provenance. Slicing the
   * stored table, rather than running the stage's SQL again, keeps the key on its row: a query without an order could
   * return its rows in another order.
   */
  rowAsTable(rowKey: string): Table | undefined;
  /**
   * Reads the rows of the stage this result shows again, from the current answers of the sources it reads, keeping
   * the rows on screen until the new ones arrive. Absent for a result no stage read produced.
   */
  readAgain?: () => void;
}

interface InteractionRequestIdentity {
  instanceId: string;
  definitionDigest: string;
  panelId: string;
  interactionId: string;
}

export type InteractionRequest = InteractionRequestIdentity &
  (
    | {
        precondition?: undefined;
        resultRevision: ResultRevision;
        subject: InteractionSubject;
      }
    | {
        precondition: MarkLineageInteractionPrecondition;
        resultRevision: ResultRevision;
        subject: InteractionSubject;
      }
    | {
        precondition: EntityInteractionPrecondition;
        resultRevision?: never;
        subject: Extract<InteractionSubject, { kind: "entity" }>;
      }
  );

/** What one reader of a control does once a control interaction has set the
 *  control: a SQL stage that reads it as `$name`, a control-filter stage
 *  that filters by it, or a chart spec that reads it as `$name`. */
export interface ControlReferenceEffect {
  kind: "sql" | "control-filter" | "chart-spec";
  path: (string | number)[];
  stage?: string;
  panel?: string;
  dependentPanels: string[];
  whenNoValue: "blocks-result" | "filters-nothing" | "blocks-panel";
  afterOperation:
    | "interpolated"
    | "filtered"
    | "blocked-no-value"
    | "unsupported-conversion"
    | "filters-nothing"
    | "unknown";
  message?: string;
}

/** What a control interaction does, worked out from the definition alone: the
 *  control's resolved value after the interaction, and what each stage or
 *  chart spec that reads the control does then. */
export interface ControlEffect {
  basis: "definition";
  control: string;
  valueType: InteractionValueType;
  multi: boolean;
  defaultValues: SerializedInteractionValue[];
  valueAfter:
    | {
        operation: "explicit-empty" | "authored-default";
        status: "has-value" | "no-value";
        values: SerializedInteractionValue[];
      }
    | { operation: "depends-on-gesture" };
  referenceAnalysis: {
    coverage: "not-provided" | "partial" | "complete";
    scope: ("sql" | "control-filter" | "chart-spec")[];
    omittedPaths: (string | number)[][];
    references: ControlReferenceEffect[];
  };
}

export type InteractionPreview = {
  diagnostics: InteractionDiagnostic[];
  controlEffect?: ControlEffect;
  resultContext?: ResultContext | EntityResultContext;
  targetResultContext?: ResultContext;
  /** The key values of what was picked: one tuple for each picked row, group,
   *  value tuple or entity; none for a range, which `intervals` holds. */
  values?: TaggedKeyValue[][];
  /** The range of each key, when what was picked is a range. */
  intervals?: TaggedInterval[];
} & (
  | { status: "ready" | "needs-gesture"; interaction: PanelInteraction }
  | { status: "refused"; interaction?: PanelInteraction }
);

/**
 * The detail service's check (`checkDetailTarget`) that a contributing-rows target's rows come from the data the
 * displayed result was computed from (the two read a stage in common, every stage both read is declared the same way
 * in both, and every source the target read gave both results the same fetches), and that the target applies the
 * displayed result's selections the same way. `source-refetched`: all of that holds except that a source both read
 * was fetched again for one of them; reading both results again gives both the source's current answers.
 */
export type DetailTargetCheck =
  | {
      status: "compatible";
      resultContext: ResultContext;
      fields: { field: string; valueType: InteractionValueType }[];
      exactFields?: {
        field: string;
        valueType: ExactValueType;
      }[];
    }
  | {
      status:
        | "unavailable"
        | "incompatible-result-context"
        | "source-refetched";
      diagnostics: InteractionDiagnostic[];
    };

export type InteractionOutcome =
  | { status: "committed"; sequence: number }
  | { status: "detail-requested"; request: DetailRequest }
  | {
      status: "navigation-requested";
      destination: NavigateDestination;
      values: TaggedKeyValue[];
      timeRange?: ResultContext["timeRange"];
    }
  | { status: "refused"; diagnostics: InteractionDiagnostic[] };

export interface DetailRequest {
  interaction: Extract<PanelInteraction, { kind: "detail" }>;
  subject: InteractionSubject;
  displayed: ResultRef;
  target?: ResultRef;
}

export interface DetailPageRequest {
  offset: number;
  limit: number;
}

export interface DetailPage {
  /** The page's rows as an Arrow table, which keeps struct, list and binary cells and exact scalars unconverted. */
  table: Table;
  totalRows: number;
}

export interface RetainedDetailRows {
  resultContext: ResultContext;
  readPage(
    request: DetailPageRequest,
    signal: AbortSignal,
  ): Promise<DetailPage>;
  exportRows(signal: AbortSignal): Promise<DetailPage>;
  release(): Promise<void>;
}

export type DetailOutcome =
  | { status: "ready"; rows: RetainedDetailRows }
  | {
      status:
        | "expired"
        | "unavailable"
        | "incompatible-result-context"
        | "source-refetched";
      diagnostics: InteractionDiagnostic[];
    };

export interface DetailService {
  /** Retains the displayed result, and for `contributing-rows` the target result, before this method first awaits. */
  prepare(request: DetailRequest, signal?: AbortSignal): Promise<DetailOutcome>;
}

export interface SerializedInteractionState {
  version: 2;
  controlValues: Record<string, SerializedInteractionValue[] | undefined>;
  selections: {
    name: string;
    scope: string;
    source: string;
    clients: string[];
    clause: SerializedSelectionClause;
  }[];
  timeRange?: { startMs: number; endMs: number };
}

export interface CommittedInteractionSnapshot
  extends SerializedInteractionState {
  sequence: number;
}
